/**
 * Color Utility Functions
 *
 * Shared utilities for color conversion used by Review Alert components.
 *
 * @since 2.5.0
 */

/**
 * Convert hex color to HSL hue value (0-360)
 *
 * Used to derive hue from accent_color for OKLCH theming when accent_hue is not saved.
 *
 * @param {string} hex - Hex color code (e.g., '#FF0000' or 'FF0000')
 * @returns {number} Hue value (0-360), defaults to 220 (blue) for invalid input
 */
export function hexToHue( hex ) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec( hex );
    if ( !result ) return 220; // Default blue hue (matches CSS default --sbr-popup-accent-hue)

    // Parse RGB values (0-1 range)
    const r = parseInt( result[1], 16 ) / 255;
    const g = parseInt( result[2], 16 ) / 255;
    const b = parseInt( result[3], 16 ) / 255;

    const max = Math.max( r, g, b );
    const min = Math.min( r, g, b );
    const delta = max - min;

    let h = 0;

    if ( delta === 0 ) {
        h = 0; // Achromatic (gray)
    } else if ( max === r ) {
        h = 60 * ( ( ( g - b ) / delta ) % 6 );
    } else if ( max === g ) {
        h = 60 * ( ( ( b - r ) / delta ) + 2 );
    } else {
        h = 60 * ( ( ( r - g ) / delta ) + 4 );
    }

    if ( h < 0 ) h += 360;

    return Math.round( h );
}

/**
 * Convert HSL hue to hex color
 *
 * Uses full saturation (100%) and 50% lightness for vibrant colors.
 *
 * @param {number} h - Hue value (0-360)
 * @returns {string} Hex color code (e.g., '#FF0000')
 */
export function hueToHex( h ) {
    // HSL to RGB conversion with S=100%, L=50%
    const s = 1; // 100% saturation
    const l = 0.5; // 50% lightness

    const c = ( 1 - Math.abs( 2 * l - 1 ) ) * s;
    const x = c * ( 1 - Math.abs( ( ( h / 60 ) % 2 ) - 1 ) );
    const m = l - c / 2;

    let r = 0, g = 0, b = 0;

    if ( h >= 0 && h < 60 ) {
        r = c; g = x; b = 0;
    } else if ( h >= 60 && h < 120 ) {
        r = x; g = c; b = 0;
    } else if ( h >= 120 && h < 180 ) {
        r = 0; g = c; b = x;
    } else if ( h >= 180 && h < 240 ) {
        r = 0; g = x; b = c;
    } else if ( h >= 240 && h < 300 ) {
        r = x; g = 0; b = c;
    } else {
        r = c; g = 0; b = x;
    }

    r = Math.round( ( r + m ) * 255 );
    g = Math.round( ( g + m ) * 255 );
    b = Math.round( ( b + m ) * 255 );

    return `#${r.toString( 16 ).padStart( 2, '0' )}${g.toString( 16 ).padStart( 2, '0' )}${b.toString( 16 ).padStart( 2, '0' )}`.toUpperCase();
}
